/**
 * Mitarbeiteransicht (Phase 7 von #763): eine zweite, flache Ansicht neben dem Wochenplan — jede/r
 * aktive Mitarbeitende erscheint genau einmal (dedup't über alle Postens/Bereiche hinweg, nicht wie im
 * Wochenplan einmal pro zugeordnetem Posten), alle Schichten der Woche werden in dieser einen Zeile
 * zusammengeführt. Siehe reference/crispy-wiggling-balloon.md Phase 7 ("Mitarbeiteransicht variant A").
 *
 * Teilt sich die komplette Chrome (Appbar/Toolbar/Filterbar/Filter-Panel/Vorlagen-Rail) mit dem
 * Wochenplan — nur der Grid-Inhalt (#magrid-content statt #grid-content) unterscheidet sich. Nutzt
 * dafür bewusst denselben leeren-String-Posten-Sentinel wie addGridRow/zeigeMitarbeiterZelle: eine
 * Zeile ohne festen Posten-Bezug bekommt IDs wie `ma::<MitarbeiterID>`/`1::<MitarbeiterID>` — dadurch
 * lassen sich addGridRow/zeigeMitarbeiterZelle/zeigeMitarbeiterStunden/zeigeSchichtAddenControls aus
 * dienstplan.js unverändert wiederverwenden (siehe dort für Details).
 */

import dayjs from '../../../../shared/services/dayjs.js';
import { schichtenService } from '../services/SchichtenService.js';
import { stammdatenService } from '../services/StammdatenService.js';
import { datumService } from '../services/DatumService.js';
import { Egfz } from '../../../../shared/services/Egfz.js';
import { Roles } from '../../../../../domain/konstanten/Roles.js';
import { findePosten } from '../util.js';
import { createSchichtElement } from '../components/shiftCard.js';
import { farbeFuerBereich } from '../util/bereichFarben.js';
import { fuelleWetterBadges } from '../components/weatherBadge.js';
import { erstelleBedarfZeile } from '../components/bedarfZeile.js';
import {
	istBereichSichtbar,
	istPostenSichtbar,
	istEinsatzortSichtbar,
	istQualifikationSichtbar,
	istSucheTreffer,
	renderFilterPanel,
} from '../components/filterPanel.js';
import {
	addGridRow,
	zeigeMitarbeiterZelle,
	erstelleTagesnotizenZeile,
	zeigeSchichtAddenControls,
	filterMitarbeiterAktiv,
	showGridLoader,
	hideGridLoader,
	aktualisiereKwAnzeige,
	passeVorlagenBereichAn,
	erstelleStatusKopf,
	wendeBedarfAenderungAn,
	ladeStundenMonat,
	zeigePostenSummen,
	MA_GESAMT_ID,
} from '../dienstplan.js';

const magrid = document.getElementById('magrid');
const magridContent = document.getElementById('magrid-content');

/**
 * Eine Mitarbeiterzeile ist sichtbar, wenn mindestens einer ihrer Postens (zugeordnet ODER diese Woche
 * beschichtet, siehe schichtenPostenByMA) den Bereichs-/Posten-Filter passiert, UND ihre eigenen
 * Einsatzorte/Qualifikationen den jeweiligen Filter passieren — Posten-Filter gelten hier also über die
 * Vereinigung aller Postens der Person statt (wie im Wochenplan) pro einzelner Posten-Zeile.
 * @param {object} m
 * @param {array<object>} postenListe
 * @param {Map<string, Set<string>>} schichtenPostenByMA
 * @param {array<object>} schichtenDerPerson alle Schichten der Person diese Woche (für die Suche — hier zählt
 *   jeder Schichttitel, da es nur eine Zeile pro Person gibt)
 */
function istMitarbeiterSichtbar(m, postenListe, schichtenPostenByMA, schichtenDerPerson = []) {
	const aktivePosIds = m.Beschaeftigung[0].Posten.map((p) => p.PostenID);
	const maPosten = schichtenPostenByMA.get(m._id) ?? new Set();
	const allePostenIDs = new Set([...aktivePosIds, ...maPosten]);
	const hatSichtbarenPosten = [...allePostenIDs].some((postenID) => {
		const posten = findePosten(postenListe, postenID);
		return posten?._id && istBereichSichtbar(posten.BereichID) && istPostenSichtbar(posten._id);
	});
	const aktiveQualifikationen = m.Beschaeftigung[0].Verguetung.filter((v) => v.Verwenden).map((v) => v.QualifikationID);
	const aktiveEinsatzorte = (m.Beschaeftigung[0].Einsatzorte ?? []).map((e) => e.EinsatzortID);
	return hatSichtbarenPosten
		&& istQualifikationSichtbar(aktiveQualifikationen)
		&& istEinsatzortSichtbar(aktiveEinsatzorte)
		&& istSucheTreffer(m, schichtenDerPerson);
}

/**
 * Baut die Spaltenköpfe (Mitarbeiter | Status | 7×Tag | Woche) direkt unter der Tagesnotizen-Zeile.
 * Eigene Zell-IDs (`mahead:date:N`), da `date:N` bereits vom Wochenplan-Header belegt ist.
 */
function erstelleMaSpaltenkoepfe() {
	const lblKopf = document.createElement('div');
	lblKopf.className = 'colhead lbl';
	lblKopf.innerText = 'Mitarbeiter';
	magridContent.appendChild(lblKopf);
	magridContent.appendChild(erstelleStatusKopf());

	const heute = dayjs.tz(new Date(), dayjs.tz.DEFAULT_TZ).startOf('day');
	datumService.holeWochenDaten().forEach((tag, i) => {
		const tagNr = i === 6 ? 7 : i + 1; // Montag=1 .. Sonntag=7, wie im Rest des Grids
		const istWochenende = i >= 5;
		const istHeute = tag.isSame(heute, 'day');
		const kopf = document.createElement('div');
		kopf.className = `colhead${istWochenende ? ' we' : ''}${istHeute ? ' today' : ''}`;
		kopf.id = `mahead:date:${tagNr}`;
		kopf.innerHTML = `${tag.format('dd')}<small>${tag.format('DD.MM.')}</small>`;
		// Platzhalter fürs Wetter-Icon, wird von fuelleWetterBadges() unten befüllt (document-weite
		// [data-wetter-tag]-Abfrage, kennt keinen Unterschied zwischen den beiden Grids).
		const wetterBadge = document.createElement('span');
		wetterBadge.className = 'wx';
		wetterBadge.setAttribute('data-wetter-tag', tag.format('YYYY-MM-DD'));
		kopf.appendChild(wetterBadge);
		magridContent.appendChild(kopf);
	});
	const wocheKopf = document.createElement('div');
	wocheKopf.className = 'colhead num';
	wocheKopf.innerText = window.myVars.UserRoles.includes(Roles.Sachbearbeiter) || window.myVars.UserRoles.includes(Roles.DienstplanerMitBudget) ? 'Stunden\nKosten' : 'Stunden';
	magridContent.appendChild(wocheKopf);
	// Absichtlich nicht awaited, siehe dienstplan.js:zeigeWochenDaten.
	fuelleWetterBadges();
}

/**
 * Feiertags-Label im Header + Feiertags-Hervorhebung der Tageszellen, analog zu dienstplan.js:
 * zeigeFeiertage — dort aber hart auf die Wochenplan-Header-IDs (`#date:N`) verdrahtet, daher hier
 * eine eigene, auf #magrid-content beschränkte Variante statt eine Wiederverwendung.
 * @param {object} betriebsstaette
 */
async function zeigeFeiertageMA(betriebsstaette) {
	const feiertageBundesland = await datumService.fetchFeiertage(betriebsstaette.Bundesland, betriebsstaette.Feiertage);
	const datumStart = datumService.laufendesDatum;
	for (let i = 0; i < 7; i++) {
		const datumTmp = datumStart.add(i, 'days');
		const dateString = datumTmp.format('DD.MM.YYYY');
		const isFeiertag = feiertageBundesland[dateString];
		if (isFeiertag) {
			magridContent.querySelectorAll(`[aria-label="${dateString}"]`).forEach((item) => item.classList.add('feiertag'));
			const tagNr = datumTmp.day() === 0 ? 7 : datumTmp.day();
			const label = document.createElement('div');
			label.classList.add('feiertag-label');
			label.innerText = isFeiertag.fname;
			document.getElementById(`mahead:date:${tagNr}`).appendChild(label);
		}
	}
}

/**
 * Befüllt den Dienstplan mit allen Daten und Controls für die Mitarbeiteransicht.
 * @param {boolean} betriebWechsel siehe dienstplan.js:zeigeDienstplan
 * @param {boolean} maRefresh siehe dienstplan.js:zeigeDienstplan
 */
async function zeigeMitarbeiteransicht(betriebWechsel = false, maRefresh = false) {
	// Ladeanzeige wie im Wochenplan (dienstplan.js:zeigeDienstplan) — nur über dem Grid, nicht der
	// ganzen Seite, siehe dp-grid-loader in dienstplan.css.
	aktualisiereKwAnzeige();
	magridContent.innerHTML = '';
	magridContent.style.display = 'none';
	showGridLoader(magrid);
	document.body.style.cursor = 'wait';

	const postenListe = await stammdatenService.getPosten(betriebWechsel);
	const einsatzorte = await stammdatenService.getEinsatzorte(betriebWechsel);
	const mitarbeiterliste = await stammdatenService.getMitarbeiter(betriebWechsel || maRefresh);
	const mitarbeiterAktiv = filterMitarbeiterAktiv(mitarbeiterliste);
	const qualifikationen = await stammdatenService.getQualifikationen();
	const unternehmen = await stammdatenService.getUnternehmensdaten();
	const bereicheAktiv = unternehmen.Bereiche.filter((bereich) => bereich.Verwenden);
	const bereichePostenAktiv = bereicheAktiv.filter((bereich) => postenListe.some((posten) => posten.BereichID === bereich._id));

	renderFilterPanel(bereichePostenAktiv, postenListe, einsatzorte, qualifikationen, mitarbeiterAktiv, () => zeigeMitarbeiteransicht(true), passeVorlagenBereichAn);

	if (bereichePostenAktiv.length === 0) {
		// Der Wochenplan zeigt beim initialen Laden bereits den "keine Bereiche"-Hinweis-Dialog.
		hideGridLoader(magrid);
		document.body.style.cursor = 'default';
		return;
	}

	const [alleSchichten] = await Promise.all([schichtenService.getSchichten(datumService.laufendesDatum), ladeStundenMonat()]);
	const schichtenPostenByMA = new Map();
	const schichtenNachMA = new Map();
	for (const s of alleSchichten) {
		if (!schichtenPostenByMA.has(s.MitarbeiterID)) schichtenPostenByMA.set(s.MitarbeiterID, new Set());
		schichtenPostenByMA.get(s.MitarbeiterID).add(s.PostenID);
		if (!schichtenNachMA.has(s.MitarbeiterID)) schichtenNachMA.set(s.MitarbeiterID, []);
		schichtenNachMA.get(s.MitarbeiterID).push(s);
	}

	await erstelleTagesnotizenZeile(magridContent, 'manotiz:');
	erstelleMaSpaltenkoepfe();
	// Offener Bedarf der Woche direkt unter den Spaltenköpfen (rendert nichts ohne Bedarfszeilen).
	// Änderungen aus dem Bedarf-Dialog gezielt einsetzen (eine Zeile je Person: Zelle `${Tag}::${MitarbeiterID}`).
	await erstelleBedarfZeile(magridContent, {
		neuLaden: (aenderung) => wendeBedarfAenderungAn(aenderung, {
			gridIdFuer: (s) => `${s.Wochentag.Zahl}::${s.MitarbeiterID}`,
			neuLaden: () => zeigeMitarbeiteransicht(),
		}),
		idPrefix: 'mabedarf:',
	});

	const sichtbareMitarbeiter = mitarbeiterAktiv.filter((m) => istMitarbeiterSichtbar(m, postenListe, schichtenPostenByMA, schichtenNachMA.get(m._id) ?? []));
	for (const m of sichtbareMitarbeiter) {
		addGridRow(m._id, '', datumService.laufendesDatum, m.Uebersicht.Wunschzeiten, m.Beschaeftigung[0].Urlaubsantraege, m.Beschaeftigung[0].Eintrittsdatum, m.Beschaeftigung[0].Austrittsdatum, m.Personalien?.Geburtsdatum, undefined, magridContent);
		// Bereichsfarbe des Avatars: der erste zugeordnete (oder ersatzweise erste beschichtete) Posten
		// der Person — bei mehreren Bereichen pro Woche eindeutig einer davon, siehe Phase-7-Plan.
		const primaryPostenID = m.Beschaeftigung[0].Posten[0]?.PostenID ?? [...(schichtenPostenByMA.get(m._id) ?? [])][0];
		const bereichID = primaryPostenID ? findePosten(postenListe, primaryPostenID)?.BereichID : undefined;
		const bereichFarbe = bereichID ? farbeFuerBereich(unternehmen.Bereiche, bereichID) : undefined;
		const mitarbeiterElement = document.getElementById(`ma::${m._id}`);
		const monatElement = document.getElementById(`ms::${m._id}`);
		await zeigeMitarbeiterZelle(m, qualifikationen, alleSchichten, mitarbeiterAktiv, mitarbeiterElement, monatElement, bereichFarbe, '');
		// Alle Schichten der Person über alle Postens/Bereiche hinweg in ihrer einen Zeile anzeigen.
		alleSchichten
			.filter((s) => s.MitarbeiterID === m._id)
			.forEach((schicht) => {
				// ArbeitAmFeiertag entsteht durch Stempeln am Feiertag und wird im Dienstplan nicht angezeigt
				if (schicht.Zeitbloecke?.[0]?.EgfzID === Egfz.ArbeitAmFeiertag) return;
				createSchichtElement(schicht, `${schicht.Wochentag.Zahl}::${m._id}`);
			});
	}
	// Gesamtsumme über alle sichtbaren Mitarbeitenden, wie im Wochenplan nur für die Budget-Rollen
	if (window.myVars.UserRoles.includes(Roles.Sachbearbeiter) || window.myVars.UserRoles.includes(Roles.DienstplanerMitBudget)) {
		const sichtbareMaIDs = new Set(sichtbareMitarbeiter.map((m) => m._id));
		zeigePostenSummen(MA_GESAMT_ID, alleSchichten.filter((s) => sichtbareMaIDs.has(s.MitarbeiterID)), mitarbeiterAktiv, undefined, 'Summe', magridContent);
	}
	zeigeSchichtAddenControls();
	const auswahlBS = unternehmen.Betriebsstaette.find((bs) => bs._id === stammdatenService.aktuelleBetriebsstaette);
	await zeigeFeiertageMA(auswahlBS);
	magridContent.style.display = 'grid';
	hideGridLoader(magrid);
	document.body.style.cursor = 'default';
}

export { zeigeMitarbeiteransicht };
